import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { X } from 'lucide-react';

const CONSENT_KEY = 'bib_cookie_consent';

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });
  const location = useLocation();
  const isOps = location.pathname.startsWith('/ops');

  useEffect(() => {
    if (isOps) return;
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    } else {
      try { setPrefs(JSON.parse(stored)); } catch {}
    }
  }, []);

  const saveConsent = (consent) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    localStorage.setItem('bib_consent_date', new Date().toISOString());
    setPrefs(consent);
    setVisible(false);
  };

  const acceptAll = () => saveConsent({ necessary: true, analytics: true, marketing: true });
  const acceptNecessary = () => saveConsent({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => saveConsent(prefs);

  if (!visible || isOps) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6" data-testid="cookie-consent">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-200 overflow-hidden">
        {/* Main banner */}
        <div className="p-5">
          <div className="flex items-start justify-between gap-4 mb-3">
            <h3 className="text-base font-semibold text-slate-900">We value your privacy</h3>
            <button onClick={acceptNecessary} className="text-slate-400 hover:text-slate-600 transition-colors p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[13px] text-slate-500 leading-relaxed mb-4">
            We use cookies and similar technologies to improve your experience, analyze site traffic, and understand where our visitors come from. Your data is processed in accordance with Canada's <strong>Personal Information Protection and Electronic Documents Act (PIPEDA)</strong>.
            {' '}<Link to="/privacy" className="text-emerald-600 hover:text-emerald-700 underline">Privacy Policy</Link>
          </p>

          {!showDetails ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={acceptAll} className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-9 px-5 text-[13px] font-medium" data-testid="accept-all-cookies">
                Accept all
              </Button>
              <Button onClick={acceptNecessary} variant="outline" className="rounded-full h-9 px-5 text-[13px] border-slate-200" data-testid="accept-necessary-cookies">
                Necessary only
              </Button>
              <button onClick={() => setShowDetails(true)} className="text-[13px] text-slate-500 hover:text-slate-900 underline transition-colors" data-testid="customize-cookies">
                Customize
              </button>
            </div>
          ) : (
            <div>
              <div className="space-y-3 mb-4">
                {[
                  { key: 'necessary', label: 'Strictly necessary', desc: 'Required for the site to function. Cannot be disabled.', locked: true },
                  { key: 'analytics', label: 'Analytics', desc: 'Help us understand how visitors use the site (e.g., page views, Blueprint completion rates).' },
                  { key: 'marketing', label: 'Marketing', desc: 'Allow us to measure ad effectiveness and provide relevant content.' },
                ].map(item => (
                  <label key={item.key} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl cursor-pointer">
                    <input
                      type="checkbox"
                      checked={prefs[item.key]}
                      disabled={item.locked}
                      onChange={() => !item.locked && setPrefs(p => ({ ...p, [item.key]: !p[item.key] }))}
                      className="mt-0.5 rounded border-slate-300"
                    />
                    <div>
                      <p className="text-[13px] font-medium text-slate-900">{item.label}</p>
                      <p className="text-[11px] text-slate-500">{item.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex gap-3">
                <Button onClick={saveCustom} className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-9 px-5 text-[13px] font-medium" data-testid="save-cookie-prefs">
                  Save preferences
                </Button>
                <Button onClick={acceptAll} variant="outline" className="rounded-full h-9 px-5 text-[13px] border-slate-200">
                  Accept all
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
